import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createTheme, ThemeProvider as BaseThemeProvider, CssBaseline } from "@mui/material";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as colors from "../colors";
import { resolveInitialThemeMode, THEME_STORAGE_KEY, ThemeMode } from "../theme";

const cacheInstance = createCache({ key: 'css' });

const createAppTheme = (mode: ThemeMode) => createTheme({
    palette: {
        mode,
        primary: {
            main: colors.blue_1,
            contrastText: colors.white
        },
        secondary: {
            main: colors.blue_3,
            contrastText: colors.white
        },
        info: {
            main: colors.dark_blue_3,
            contrastText: colors.white
        },
        success: {
            main: colors.green_1,
            contrastText: colors.white
        },
        warning: {
            main: colors.orange_1,
            contrastText: colors.white
        },
        error: {
            main: colors.red_1,
            contrastText: colors.white
        },
        tonalOffset: 0.1
    },
    typography: {
        fontFamily: [
            "Helvetica Neue",
            "Arial"
        ].join(",")
    }
});

interface ThemeModeContextValue {
    mode: ThemeMode;
    setMode: (mode: ThemeMode) => void;
    toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>(null);
export const useThemeMode: () => ThemeModeContextValue = () => useContext(ThemeModeContext);

export const AppThemeProvider = ({ children }: { children: any }) => {
    const [mode, setMode] = useState<ThemeMode>(resolveInitialThemeMode);

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    // Keep the persisted choice and the <body> class (which drives the SCSS CSS variables in
    // src/theming/{light,dark}.scss) in sync with the active mode.
    useEffect(() => {
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, mode);
        } catch { /* localStorage unavailable - ignore */ }
        document.documentElement.setAttribute("data-theme", mode);
        document.body.className = `theme-${mode}`;
    }, [mode]);

    const contextValue = useMemo<ThemeModeContextValue>(() => ({
        mode,
        setMode,
        toggle: () => setMode(current => current === "dark" ? "light" : "dark")
    }), [mode]);

    return (
        <ThemeModeContext.Provider value={contextValue}>
            <CacheProvider value={cacheInstance}>
                <BaseThemeProvider theme={theme}>
                    <CssBaseline enableColorScheme />
                    {children}
                </BaseThemeProvider>
            </CacheProvider>
        </ThemeModeContext.Provider>
    );
};
